import { cdmApi } from './index';

export type TFile = File & { originalname?: string; hasNoProduct?: boolean; wrongCompanyId?: boolean };

export type TPictures = {
    company_id: number;
    country_id: number;
    ean: string;
    upc?: string;
    image_hash: string;
    image_url: string;
    position: number;
    image_caractersitics: 'hero' | 'fop' | 'default';
    image_type: string;
    status: 'active' | 'pending' | 'review' | 'archived';
    isFromApi: boolean;
    period_id: number;
    year: number;
    created_at: string;
    updated_at: string;
    product_name?: string;
    hasNoProduct?: boolean;
    wrongCompanyId?: boolean;
    isWarning?: boolean;
    _id?: string;
    overwrite?: boolean;
    duplicatePosition?: boolean;
    retailers_id?: number[];
    isAll?: boolean;
};

type TGetParams = Partial<Record<keyof TPictures, any>> & {
    month?: number;
    year?: number;
};

export type TPictureUploadResponse = {
    data: {
        file: TFile;
        response: {
            picture_id?: string;
            error?: string;
        };
    }[];
    message: string;
};

type TPictureGetResponse = {
    data?: TPictures[];
    message: string;
};

type TPictureDeleteResponse = {
    bucketError: {};
    data: TPictures[];
    message: string;
};

export type TPictureUpdateResponse = {
    data?: TPictures;
    message: 'failed' | 'success';
};

const PREFIX = 'pictures';

export const picturesApi = cdmApi.injectEndpoints({
    endpoints: (builder) => ({
        getAllPictures: builder.query<TPictureGetResponse, TGetParams>({
            query: (data) => ({ url: `${PREFIX}/`, method: 'get', params: data }),
            providesTags: ['Pictures'],
            transformResponse: (response: TPictureGetResponse): TPictureGetResponse => {
                const data =
                    response?.data?.map((pic) => ({
                        ...pic,
                        isWarning: pic.wrongCompanyId || pic.hasNoProduct || false,
                    })) || [];

                return { ...response, data };
            },
        }),
        downloadPictures: builder.query<any, any>({
            query: (data) => ({ url: `${PREFIX}/download`, method: 'post', data }),
        }),
        eanCheck: builder.query<TPictureUploadResponse, { company_id: number; eans: string[] }>({
            query: (data) => ({ url: `${PREFIX}/eanCheck`, method: 'post', data }),
        }),
        uploadPictures: builder.mutation<TPictureUploadResponse, FormData>({
            query: (data) => ({ url: `${PREFIX}/upload`, method: 'post', data }),
            invalidatesTags: ['Pictures'],
        }),
        updatePicture: builder.mutation<TPictureUpdateResponse, TPictures & { picture_id: string }>({
            query: (data) => ({ url: `${PREFIX}/${data.picture_id}`, method: 'patch', data }),
            invalidatesTags: ['Pictures'],
        }),
        updatePictures: builder.mutation<TPictureUpdateResponse, { pictureIds: string[]; updates: Partial<TPictures> }>(
            {
                query: (data) => ({ url: `${PREFIX}/`, method: 'patch', data }),
                invalidatesTags: ['Pictures'],
            },
        ),
        deletePictures: builder.mutation<TPictureDeleteResponse, { ids: string[]; isDelete?: boolean }>({
            query: (data) => ({ url: `${PREFIX}/`, method: 'delete', data }),
            async onQueryStarted({ ids, isDelete }, { dispatch, queryFulfilled, getState }) {
                const resp = await queryFulfilled;
                const respPicturesDeleted = resp.data.data.map((pic) => pic._id);

                const cache = cdmApi.util.selectInvalidatedBy(getState(), ['Pictures'])[0];

                const patchResult = dispatch(
                    picturesApi.util.updateQueryData('getAllPictures', cache.originalArgs, (draft) => {
                        const draftRef = draft;
                        draftRef.data = draftRef.data?.filter((picture) => !respPicturesDeleted.includes(picture._id));
                    }),
                );

                queryFulfilled.catch(() => {
                    patchResult.undo();
                });
            },
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetAllPicturesQuery,
    useLazyEanCheckQuery,
    useUploadPicturesMutation,
    useUpdatePictureMutation,
    useUpdatePicturesMutation,
    useDeletePicturesMutation,
    useLazyDownloadPicturesQuery,
} = picturesApi;
